import { useEffect, useRef } from "react";
import { useCreateOrUpdateRenewalPreference } from "../../../../services/renewal-preference/renewal-preference.service";
import { userActivityLogger } from "../../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";
import { getSpeciesName } from "../../../../components/PageTitle/WellnessPlanTitle/WellnessPlanTitle";
import { MODALS, UPSELL_RENEWAL_ENTRY_POINT_KEY, type WellnessRenewalEntryPoint } from "../../../../config/params";
import { formatBirthdate, getPrices } from "../helpers";
import { useModal } from "../../../../context/ModalContext";
import { IPetItemResponse } from "../../../../services/pet-list/pet-list.interface";
import { FlattenedPlanData } from "../../../../hooks/useWellnessPlans";
import { IPetWellnessplanDetails } from "../../../../services/wellness-plan/wellness-plan.interface";

interface IUseRenewalParams {
  petId: string | null;
  currentPet: IPetItemResponse[];
  wellnessPurchaseData: FlattenedPlanData | null;
  petWellnessPlanData: IPetWellnessplanDetails | undefined;
  billingCycle: "perYear" | "perMonth";
  prices: ReturnType<typeof getPrices>;
}

export const useWellnessRenewal = ({
  petId,
  currentPet,
  wellnessPurchaseData,
  petWellnessPlanData,
  billingCycle,
  prices,
}: IUseRenewalParams) => {
  const storedEntryPoint = localStorage.getItem(
    UPSELL_RENEWAL_ENTRY_POINT_KEY,
  ) as WellnessRenewalEntryPoint | null;

  const entryPointRef = useRef<WellnessRenewalEntryPoint | "unknown">(
    storedEntryPoint || "unknown",
  );
  const { openModal } = useModal();

  const {
    saveRenewalPreference,
    response: renewalPreferenceResponse,
    loading: renewalPreferenceLoading,
    error: renewalPreferenceError,
  } = useCreateOrUpdateRenewalPreference();

  useEffect(() => {
    if (renewalPreferenceResponse && currentPet?.[0]) {
      const currentSubscriptionId = petWellnessPlanData?.subscriptionId;
      const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

      if (renewalPreferenceResponse.success) {
        userActivityLogger(
          MIXPANEL_EVENTS.GOODPET_TO_TOTALCARE_TRANSITION_SUCCESS,
          {
            pet_id: petId,
            pet_name: currentPet[0].name,
            species: getSpeciesName(currentPet[0].species),
            old_plan_type: petWellnessPlanData?.planType,
            new_plan_name: wellnessPurchaseData?.planName,
            new_plan_type: wellnessPurchaseData?.planType,
            new_plan_code: wellnessPurchaseData?.planCode,
            new_plan_id: prices[billingCycle].planId,
            billing_cycle: billingCycle === "perYear" ? "Annual" : "Monthly",
            subscription_fee: prices[billingCycle].amount,
            enrollment_fee: prices[billingCycle].enrollmentFee,
            total_amount:
              prices[billingCycle].amount! +
              prices[billingCycle].enrollmentFee!,
            dob: formattedBirthdate,
            entry_point: entryPointRef.current,
          },
        );

        if (billingCycle === "perMonth") {
          openModal(MODALS.SCRATCHPAY_RENEWAL_PENDING_MODAL, "", {
            petId: petId,
            petName: currentPet[0].name,
            petImage: currentPet[0].image,
            currentSubscriptionId,
          });
        } else {
          openModal(MODALS.WELLNESS_PLAN_TRANSITION_SUCCESS, "", {
            currentSubscriptionId: currentSubscriptionId,
          });
        }
      } else {
        userActivityLogger(
          MIXPANEL_EVENTS.GOODPET_TO_TOTALCARE_TRANSITION_FAILURE,
          {
            pet_id: petId,
            pet_name: currentPet[0].name,
            species: getSpeciesName(currentPet[0].species),
            old_plan_type: petWellnessPlanData?.planType,
            new_plan_name: wellnessPurchaseData?.planName,
            new_plan_type: wellnessPurchaseData?.planType,
            new_plan_code: wellnessPurchaseData?.planCode,
            billing_cycle: billingCycle === "perYear" ? "Annual" : "Monthly",
            error_message:
              renewalPreferenceResponse.messages?.[0] || "Unknown error",
            entry_point: entryPointRef.current,
          },
        );
      }

      localStorage.removeItem(UPSELL_RENEWAL_ENTRY_POINT_KEY);
      entryPointRef.current = "unknown";
    }
  }, [renewalPreferenceResponse]);

  return {
    saveRenewalPreference,
    renewalPreferenceLoading,
    renewalPreferenceError,
  };
};

